/**
 * Track T8 — "Export all" one-click bundle.
 *
 * A minimal, dependency-free STORE-only (no compression) ZIP writer built by
 * hand: CRC-32 + local file headers + central directory + end-of-central-
 * directory (EOCD). STORE keeps the code tiny and auditable — the payloads are
 * SVG / CSV / XML / HTML text that a laser operator opens directly, so on-disk
 * compression buys little and a self-implemented deflate would be far larger
 * and riskier. Everything runs client-side; nothing touches the network.
 *
 * The public entry points assemble the four export artifacts a MOPA job needs —
 * the layer SVG, the CSV legend, the LightBurn `.lbrn2`, and the HTML report —
 * into ONE `.zip`, so the user gets a single save dialog instead of three or
 * four. Format builders are imported READ-ONLY from their owning tracks.
 */
import type { CutParams, PhotoResult, VectorExportContext } from "../core/types";
import { exportPhotoSvg, exportVectorSvg } from "./svgExport";
import { buildPaletteReportHtml, exportPaletteCsv } from "./paletteCsv";
import { exportLbrnCopy, exportLbrnStandalone } from "../formats/lbrnExport";
import { deriveFileName, disambiguateFileName, saveBinaryFile } from "./download";

// ---------------------------------------------------------------------------
// CRC-32 (IEEE 802.3, reflected) — table built once, lazily.
// ---------------------------------------------------------------------------

let CRC_TABLE: Uint32Array | null = null;

function crcTable(): Uint32Array {
  if (CRC_TABLE) return CRC_TABLE;
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xed_b8_83_20 ^ (c >>> 1) : c >>> 1;
    table[n] = c >>> 0;
  }
  CRC_TABLE = table;
  return table;
}

function crc32(bytes: Uint8Array): number {
  const table = crcTable();
  let c = 0xff_ff_ff_ff;
  for (let i = 0; i < bytes.length; i++) c = table[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xff_ff_ff_ff) >>> 0;
}

// ---------------------------------------------------------------------------
// DOS date/time — a fixed, deterministic timestamp keeps the zip byte-stable
// (helpful for tests + reproducible downloads). 1980-01-01 00:00:00 is the ZIP
// epoch (date/time both zero is technically invalid; use the minimum legal one).
// ---------------------------------------------------------------------------

const DOS_TIME = 0; // 00:00:00
const DOS_DATE = 0x00_21; // 1980-01-01 (year=0, month=1, day=1)

const UTF8 = new TextEncoder();

/** One entry to place in the archive. `name` is the in-zip path (forward slashes). */
export interface BundleFile {
  name: string;
  /** Text content is UTF-8 encoded; raw bytes are stored verbatim. */
  data: string | Uint8Array;
}

interface Prepared {
  nameBytes: Uint8Array;
  data: Uint8Array;
  crc: number;
  offset: number;
}

function le16(view: DataView, off: number, value: number): void {
  view.setUint16(off, value & 0xff_ff, true);
}

function le32(view: DataView, off: number, value: number): void {
  view.setUint32(off, value >>> 0, true);
}

/**
 * Assemble a STORE-only ZIP from the given files. Returns the raw archive
 * bytes. Pure + deterministic (fixed timestamp), so it is unit-testable and
 * reproducible. Duplicate/empty names are the caller's responsibility — the
 * bundle assemblers below guarantee unique, collision-free names.
 */
export function buildBundleZip(files: readonly BundleFile[]): Uint8Array {
  const LOCAL_SIG = 0x04_03_4b_50;
  const CDH_SIG = 0x02_01_4b_50;
  const EOCD_SIG = 0x06_05_4b_50;

  const prepared: Prepared[] = [];
  let localSize = 0;

  // Defensive: a valid archive must not carry two entries with the same path.
  // The assemblers below already use distinct suffixes, but disambiguate here
  // so any caller passing a duplicate name gets "_2"/"_3" rather than a broken
  // zip (two central-directory records pointing at the same name).
  const usedNames = new Set<string>();

  // First pass: encode payloads + CRCs and size the local-header section.
  for (const f of files) {
    const nameBytes = UTF8.encode(disambiguateFileName(f.name, usedNames));
    const data = typeof f.data === "string" ? UTF8.encode(f.data) : f.data;
    prepared.push({ nameBytes, data, crc: crc32(data), offset: localSize });
    localSize += 30 + nameBytes.length + data.length; // local header + name + data
  }

  // Size the central directory.
  let centralSize = 0;
  for (const p of prepared) centralSize += 46 + p.nameBytes.length;

  const total = localSize + centralSize + 22; // + EOCD
  const out = new Uint8Array(total);
  const view = new DataView(out.buffer);
  let pos = 0;

  // Local file headers + data.
  for (const p of prepared) {
    le32(view, pos, LOCAL_SIG);
    le16(view, pos + 4, 20); // version needed
    le16(view, pos + 6, 0x08_00); // general purpose: bit 11 = UTF-8 names
    le16(view, pos + 8, 0); // method 0 = STORE
    le16(view, pos + 10, DOS_TIME);
    le16(view, pos + 12, DOS_DATE);
    le32(view, pos + 14, p.crc);
    le32(view, pos + 18, p.data.length); // compressed size == uncompressed
    le32(view, pos + 22, p.data.length);
    le16(view, pos + 26, p.nameBytes.length);
    le16(view, pos + 28, 0); // extra field length
    pos += 30;
    out.set(p.nameBytes, pos);
    pos += p.nameBytes.length;
    out.set(p.data, pos);
    pos += p.data.length;
  }

  // Central directory.
  const cdStart = pos;
  for (const p of prepared) {
    le32(view, pos, CDH_SIG);
    le16(view, pos + 4, 20); // version made by
    le16(view, pos + 6, 20); // version needed
    le16(view, pos + 8, 0x08_00); // UTF-8 names
    le16(view, pos + 10, 0); // STORE
    le16(view, pos + 12, DOS_TIME);
    le16(view, pos + 14, DOS_DATE);
    le32(view, pos + 16, p.crc);
    le32(view, pos + 20, p.data.length);
    le32(view, pos + 24, p.data.length);
    le16(view, pos + 28, p.nameBytes.length);
    le16(view, pos + 30, 0); // extra field length
    le16(view, pos + 32, 0); // comment length
    le16(view, pos + 34, 0); // disk number start
    le16(view, pos + 36, 0); // internal attrs
    le32(view, pos + 38, 0); // external attrs
    le32(view, pos + 42, p.offset); // local header offset
    pos += 46;
    out.set(p.nameBytes, pos);
    pos += p.nameBytes.length;
  }

  // End of central directory.
  le32(view, pos, EOCD_SIG);
  le16(view, pos + 4, 0); // this disk
  le16(view, pos + 6, 0); // cd start disk
  le16(view, pos + 8, prepared.length); // entries on this disk
  le16(view, pos + 10, prepared.length); // total entries
  le32(view, pos + 12, centralSize);
  le32(view, pos + 16, cdStart);
  le16(view, pos + 20, 0); // comment length

  return out;
}

// ---------------------------------------------------------------------------
// Bundle assembly — gather the export artifacts under collision-free names.
// ---------------------------------------------------------------------------

const README_TEXT = (stem: string) =>
  [
    "MOPA Pixelator Web — export bundle",
    "",
    "This archive contains everything needed to run and reproduce this job:",
    `  ${stem}_layers.svg    — the color-layer vector, one <g> per C-layer`,
    `  ${stem}_legend.csv    — full per-layer MOPA record (units line + machine/material context)`,
    `  ${stem}.lbrn2         — LightBurn project with the layers + parameter seeds`,
    `  ${stem}_report.html   — printable palette sheet with a calibration checklist`,
    "",
    "Everything was generated in your browser. Nothing was uploaded.",
    "MOPA colors are machine/material/focus specific — calibrate with a test grid first.",
  ].join("\n");

export interface BundleContext {
  machine?: string;
  material?: string;
  finish?: string;
}

/**
 * Build the photo-workflow "Export all" bundle: layer SVG + CSV legend +
 * `.lbrn2` + HTML report + a short README, under one collision-free stem.
 * Returns the raw zip bytes and the suggested archive filename.
 */
export function buildPhotoBundle(
  result: PhotoResult,
  options: {
    optimizeSquareRuns: boolean;
    cutParams?: Record<number, CutParams>;
    lbrnCtx: VectorExportContext;
    context?: BundleContext;
  },
): { bytes: Uint8Array; fileName: string } {
  const stem = deriveFileName(result.sourceName, "");
  const ctx = options.context;
  const files: BundleFile[] = [
    { name: `${stem}_layers.svg`, data: exportPhotoSvg(result, { optimizeSquareRuns: options.optimizeSquareRuns, cutParams: options.cutParams, context: ctx }) },
    { name: `${stem}_legend.csv`, data: exportPaletteCsv(result, options.cutParams, ctx) },
    { name: `${stem}.lbrn2`, data: exportLbrnStandalone(options.lbrnCtx) },
    { name: `${stem}_report.html`, data: buildPaletteReportHtml(result, { machine: ctx?.machine ?? "", material: ctx?.material ?? "" }, options.cutParams) },
    { name: "README.txt", data: README_TEXT(stem) },
  ];
  return { bytes: buildBundleZip(files), fileName: `${stem}_bundle.zip` };
}

/**
 * Build the vector-workflow "Export all" bundle. Always includes the pixelated
 * SVG + `.lbrn2`; when a photo-fill palette result is supplied it also carries
 * the CSV legend + HTML report so the color-filled job is fully reproducible.
 */
export function buildVectorBundle(
  ctx: VectorExportContext,
  options: {
    sourceName: string;
    originalXml?: string;
    replaceOriginal?: boolean;
    selectedLayers?: ReadonlySet<number>;
    /** Photo-fill result for the CSV legend + report (color-fill jobs only). */
    photoResult?: PhotoResult;
    cutParams?: Record<number, CutParams>;
    context?: BundleContext;
  },
): { bytes: Uint8Array; fileName: string } {
  const stem = deriveFileName(options.sourceName, "");
  const context = options.context;
  const lbrn =
    options.originalXml != null
      ? exportLbrnCopy(options.originalXml, ctx, {
          replaceOriginal: options.replaceOriginal ?? false,
          selectedLayers: options.selectedLayers ?? new Set<number>(),
        })
      : exportLbrnStandalone(ctx);

  const files: BundleFile[] = [
    { name: `${stem}_pixelated.svg`, data: exportVectorSvg(ctx) },
    { name: `${stem}_pixelated.lbrn2`, data: lbrn },
  ];
  if (options.photoResult) {
    files.push(
      { name: `${stem}_legend.csv`, data: exportPaletteCsv(options.photoResult, options.cutParams, context) },
      { name: `${stem}_report.html`, data: buildPaletteReportHtml(options.photoResult, { machine: context?.machine ?? "", material: context?.material ?? "" }, options.cutParams) },
    );
  }
  files.push({ name: "README.txt", data: README_TEXT(stem) });
  return { bytes: buildBundleZip(files), fileName: `${stem}_bundle.zip` };
}

/**
 * Save an already-assembled bundle. Returns false when the user cancels the
 * save dialog. Kept separate from the builders so callers can test byte output
 * without touching the DOM.
 */
export function saveBundle(bytes: Uint8Array, fileName: string): Promise<boolean> {
  return saveBinaryFile(bytes, fileName, "application/zip", ".zip");
}

/** Convenience: build the photo bundle and immediately save it. */
export async function exportAllPhotoBundle(
  result: PhotoResult,
  options: Parameters<typeof buildPhotoBundle>[1],
): Promise<boolean> {
  const { bytes, fileName } = buildPhotoBundle(result, options);
  return saveBundle(bytes, fileName);
}

/** Convenience: build the vector bundle and immediately save it. */
export async function exportAllVectorBundle(
  ctx: VectorExportContext,
  options: Parameters<typeof buildVectorBundle>[1],
): Promise<boolean> {
  const { bytes, fileName } = buildVectorBundle(ctx, options);
  return saveBundle(bytes, fileName);
}
